import { Form, Radio } from 'antd';
import { useState } from 'react';
import FormField from './FormField';
import FormWrapper from './FormWrapper';
import CredentialSecurityNotice from './CredentialSecurityNotice';
import ConnectionTestButton from './ConnectionTestButton';
import { hostField, portField, usernameField, passwordField, privateKeyField } from './fieldConfigs';
import type { FieldConfig } from './types';
import type { SftpConfig } from '../../types';
import { transformSftpConfigForTest } from '../../utils/connectionConfigTransformers';
import { useConnectionTest } from '../../hooks/useConnectionTest';

const remotePathField: FieldConfig = {
    name: 'remotePath',
    label: 'Remote Path',
    type: 'text',
    required: true,
    placeholder: '/data/files',
};

interface SftpConfigFormProps {
    onConnectionTestSuccess?: () => void;
}

export default function SftpConfigForm({ onConnectionTestSuccess }: SftpConfigFormProps) {
    const form = Form.useFormInstance();
    const initialConfig = form.getFieldValue('configuration');
    const initialAuthMethod: 'password' | 'privateKey' = initialConfig?.privateKey ? 'privateKey' : 'password';
    const [authMethod, setAuthMethod] = useState<'password' | 'privateKey'>(initialAuthMethod);

    const { getConfig, handleTestConnection, isLoading } = useConnectionTest<SftpConfig>(
        transformSftpConfigForTest,
        config => Boolean(config?.host && config?.port && config?.username && config?.password && config?.remotePath),
        onConnectionTestSuccess
    );

    return (
        <FormWrapper>
            <CredentialSecurityNotice />
            <FormField config={{ ...hostField, placeholder: 'sftp.example.com' }} />
            <FormField config={portField} />
            <FormField config={usernameField} />

            <Form.Item
                label="Authentication Method"
                className="mb-2"
            >
                <Radio.Group
                    value={authMethod}
                    onChange={e => setAuthMethod(e.target.value as 'password' | 'privateKey')}
                >
                    <Radio value="password">Password</Radio>
                    <Radio value="privateKey">Private Key</Radio>
                </Radio.Group>
            </Form.Item>

            {authMethod === 'password' ? <FormField config={passwordField} /> : <FormField config={privateKeyField} />}

            <FormField config={remotePathField} />

            {onConnectionTestSuccess && authMethod === 'password' && (
                <ConnectionTestButton
                    getConfig={getConfig}
                    testConnection={handleTestConnection}
                    isLoading={isLoading}
                    requiredFieldsMessage="Please fill in all required fields (host, port, username, password, remote path) before testing the connection"
                />
            )}
        </FormWrapper>
    );
}
